import { useState, useEffect } from "react";
import { useAuth } from "@context/AuthContext";
import { apiGetMyClients, apiCreateClient, apiRemoveClientAccess } from "@services/api";
import { Modal, ModalFooter } from "@common/Modal";

const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-GB", { day:"2-digit", month:"short", year:"numeric" }) : "—";
const AVATAR_COLORS = ["#2563EB","#16A34A","#F59E0B","#EC4899","#06B6D4","#7C3AED","#DC2626","#F97316"];

export default function AccessControlPage() {
  const { userSlug, user } = useAuth();
  const [clients,   setClients]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [saving,    setSaving]    = useState(false);
  const [deleting,  setDeleting]  = useState(false);
  const [form,      setForm]      = useState({ firstName: "", lastName: "", email: "" });
  const [errors,    setErrors]    = useState({});
  const [apiError,  setApiError]  = useState("");

  const set = (k, v) => { setForm(f=>({...f,[k]:v})); setErrors(e=>({...e,[k]:""})); };

  const load = () => {
    if (!userSlug) return;
    setLoading(true);
    apiGetMyClients(userSlug).then(data => {
      setClients(Array.isArray(data?.clientList) ? data.clientList : Array.isArray(data?.clients) ? data.clients : Array.isArray(data?.data) ? data.data : []);
      setLoading(false);
    });
  };

  useEffect(() => { load(); }, [userSlug]);

  const validate = () => {
    const e = {};
    if (!form.firstName.trim()) e.firstName = "First name is required";
    if (!form.lastName.trim())  e.lastName  = "Last name is required";
    if (!form.email.trim())     e.email     = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Enter a valid email";
    setErrors(e);
    return !Object.keys(e).length;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    setApiError("");
    const res = await apiCreateClient({ user_slug: userSlug, first_name: form.firstName, last_name: form.lastName, email: form.email });
    setSaving(false);
    if (res && Number(res.status) === 1) { setShowModal(false); setForm({ firstName:"", lastName:"", email:"" }); load(); }
    else setApiError(res?.message || "Failed to create client. Please try again.");
  };

  const handleDelete = async () => {
    if (!deleteItem) return;
    setDeleting(true);
    const relation_id = deleteItem.relation_id || deleteItem.id;
    await apiRemoveClientAccess({ user_slug: userSlug, relation_id });
    setDeleting(false);
    setDeleteItem(null);
    load();
  };

  const hasError = k => errors[k];
  const fieldStyle = k => ({
    borderColor: hasError(k) ? "#DC2626" : "var(--fc-border)",
    boxShadow: hasError(k) ? "0 0 0 3px rgba(239,68,68,0.1)" : "none",
    transition: "border-color 0.18s, box-shadow 0.18s"
  });

  return (
    <div>
      {/* Header */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:24, flexWrap:"wrap", gap:12 }}>
        <div>
          <h1 style={{ fontSize:20, fontWeight:700, color:"var(--fc-text-primary)", margin:0 }}>My Clients</h1>
          <p style={{ fontSize:13, color:"var(--fc-text-muted)", margin:"3px 0 0" }}>{clients.length} client{clients.length!==1?"s":""} in your network</p>
        </div>
        <button onClick={() => { setShowModal(true); setApiError(""); setErrors({}); setForm({ firstName:"", lastName:"", email:"" }); }}
          className="fc-btn fc-btn-md fc-btn-primary fc-btn-pill">
          <i className="fa-solid fa-plus" style={{fontSize:12}} /> Create Client
        </button>
      </div>

      {/* Table */}
      <div style={{ background:"var(--fc-card-bg)", border:"1px solid var(--fc-border)", borderRadius:16, overflow:"hidden", boxShadow:"0 1px 4px rgba(0,0,0,0.05)" }}>
        <div style={{ display:"grid", gridTemplateColumns:"2fr 2.5fr 1.2fr 40px", gap:16, padding:"11px 20px", background:"var(--fc-bg-light)", borderBottom:"1px solid var(--fc-border)" }}>
          {["Client","Email","Joined",""].map((h,i) => (
            <div key={i} style={{ fontSize:11, fontWeight:700, color:"var(--fc-text-subtle)", textTransform:"uppercase", letterSpacing:"0.07em" }}>{h}</div>
          ))}
        </div>

        {loading ? (
          <div style={{ padding:"24px 20px", display:"flex", flexDirection:"column", gap:10 }}>
            {[1,2,3,4].map(i => <div key={i} style={{ height:48, background:"var(--fc-bg-light)", borderRadius:10 }} />)}
          </div>
        ) : clients.length === 0 ? (
          <div style={{ padding:"48px 20px", textAlign:"center" }}>
            <div style={{ width:52, height:52, borderRadius:14, background:"var(--fc-primary-light)", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 14px" }}>
              <i className="fa-solid fa-users" style={{ color:"#2563EB", fontSize:22 }} />
            </div>
            <div style={{ fontSize:15, fontWeight:700, color:"var(--fc-text-primary)", marginBottom:6 }}>No clients yet</div>
            <div style={{ fontSize:13, color:"var(--fc-text-subtle)" }}>Create your first client to get started</div>
          </div>
        ) : clients.map((c, i) => {
          const name = c.full_name || `${c.first_name||""} ${c.last_name||""}`.trim() || c.name || "Unknown";
          const email = c.user_email || c.email || c.contact_email || "—";
          const joined = c.added_on || c.created_at || c.join_date;
          const av = AVATAR_COLORS[(parseInt(c.relation_id || c.id) || i) % AVATAR_COLORS.length];
          return (
            <div key={c.id || i}
              style={{ display:"grid", gridTemplateColumns:"2fr 2.5fr 1.2fr 40px", gap:16, padding:"14px 20px", borderBottom: i<clients.length-1 ? "1px solid var(--fc-border-light)" : "none", alignItems:"center", transition:"background 0.15s" }}
              onMouseEnter={e => e.currentTarget.style.background="var(--fc-bg-light)"}
              onMouseLeave={e => e.currentTarget.style.background=""}>
              <div style={{ display:"flex", alignItems:"center", gap:10, minWidth:0 }}>
                <div style={{ width:36, height:36, borderRadius:10, background:av+"18", border:`1.5px solid ${av}30`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                  <i className="fa-solid fa-user" style={{ fontSize:14, color:av }} />
                </div>
                <div style={{ fontSize:14, fontWeight:600, color:"var(--fc-text-primary)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{name}</div>
              </div>
              <div style={{ fontSize:13, color:"var(--fc-text-muted)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                <i className="fa-solid fa-envelope" style={{ fontSize:11, marginRight:6, color:"var(--fc-text-hint)" }} />{email}
              </div>
              <div style={{ fontSize:13, color:"var(--fc-text-muted)" }}>
                <i className="fa-solid fa-calendar-days" style={{ fontSize:11, marginRight:6, color:"var(--fc-text-hint)" }} />{fmtDate(joined)}
              </div>
              <div>
                <button onClick={() => setDeleteItem(c)}
                  style={{ width:30, height:30, borderRadius:8, border:"1px solid var(--fc-border)", background:"var(--fc-bg-light)", display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", color:"var(--fc-text-muted)", fontSize:12, transition:"all 0.15s" }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor="var(--fc-red-border)"; e.currentTarget.style.color="#DC2626"; e.currentTarget.style.background="var(--fc-red-light)"; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor="var(--fc-border)"; e.currentTarget.style.color="var(--fc-text-muted)"; e.currentTarget.style.background="var(--fc-bg-light)"; }}>
                  <i className="fa-solid fa-trash" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {clients.length > 0 && (
        <div style={{ marginTop:12, fontSize:12, color:"var(--fc-text-subtle)", textAlign:"right" }}>
          {clients.length} client{clients.length!==1?"s":""}
        </div>
      )}

      {/* Create Modal */}
      {showModal && (
        <Modal
          title={
            <div style={{ display:"flex", alignItems:"center", gap:10 }}>
              <div style={{ width:36, height:36, borderRadius:10, background:"var(--fc-primary-light)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                <i className="fa-solid fa-user-plus" style={{ color:"#2563EB", fontSize:15 }} />
              </div>
              <div>
                <div style={{ fontSize:15, fontWeight:700, color:"var(--fc-text-primary)" }}>Create Client</div>
                <div style={{ fontSize:11, color:"var(--fc-text-subtle)", marginTop:2 }}>Add a new client to your network</div>
              </div>
            </div>
          }
          onClose={() => setShowModal(false)}
          maxWidth={440}
          footer={
            <ModalFooter
              onCancel={() => setShowModal(false)}
              onConfirm={handleSave}
              confirmBusy={saving}
              confirmLabel={saving
                ? <><i className="fa-solid fa-spinner fa-spin" style={{fontSize:11}} /> Creating...</>
                : <><i className="fa-solid fa-plus" style={{fontSize:11}} /> Create Client</>}
            />
          }>

          {apiError && (
            <div style={{ display:"flex", alignItems:"center", gap:8, padding:"10px 14px", background:"var(--fc-red-light)", border:"1px solid var(--fc-red-border)", borderRadius:10, marginBottom:16, fontSize:13, color:"#DC2626" }}>
              <i className="fa-solid fa-circle-exclamation" />{apiError}
            </div>
          )}

          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:14 }}>
            {[["firstName","First Name"],["lastName","Last Name"]].map(([k,l]) => (
              <div key={k}>
                <label style={{ fontSize:12, fontWeight:700, color:"var(--fc-text-secondary)", display:"block", marginBottom:5, textTransform:"uppercase", letterSpacing:"0.05em" }}>
                  {l} <span style={{color:"#DC2626"}}>*</span>
                </label>
                <input value={form[k]} onChange={e => set(k, e.target.value)} placeholder={l}
                  style={{ width:"100%", boxSizing:"border-box", padding:"10px 12px", border:`1.5px solid ${hasError(k)?"#DC2626":"var(--fc-border)"}`, borderRadius:10, fontSize:14, outline:"none", fontFamily:"inherit", color:"var(--fc-text-primary)", background:"var(--fc-card-bg)", boxShadow: hasError(k)?"0 0 0 3px rgba(239,68,68,0.1)":"none", transition:"all 0.18s" }}
                  onFocus={e => { if (!hasError(k)) { e.target.style.borderColor="#2563EB"; e.target.style.boxShadow="0 0 0 3px rgba(79,70,229,0.1)"; } }}
                  onBlur={e  => { if (!hasError(k)) { e.target.style.borderColor="var(--fc-border)"; e.target.style.boxShadow="none"; } }} />
                {hasError(k) && <div style={{ fontSize:12, color:"#DC2626", marginTop:4, display:"flex", alignItems:"center", gap:4 }}><i className="fa-solid fa-circle-exclamation" style={{fontSize:10}} />{errors[k]}</div>}
              </div>
            ))}
          </div>

          <div>
            <label style={{ fontSize:12, fontWeight:700, color:"var(--fc-text-secondary)", display:"block", marginBottom:5, textTransform:"uppercase", letterSpacing:"0.05em" }}>
              Email Address <span style={{color:"#DC2626"}}>*</span>
            </label>
            <div style={{ position:"relative" }}>
              <i className="fa-solid fa-envelope" style={{ position:"absolute", left:12, top:"50%", transform:"translateY(-50%)", color: hasError("email")?"#DC2626":"var(--fc-text-subtle)", fontSize:13, pointerEvents:"none" }} />
              <input type="email" value={form.email} onChange={e => set("email", e.target.value)} placeholder="client@company.com"
                style={{ width:"100%", boxSizing:"border-box", padding:"10px 12px 10px 36px", border:`1.5px solid ${hasError("email")?"#DC2626":"var(--fc-border)"}`, borderRadius:10, fontSize:14, outline:"none", fontFamily:"inherit", color:"var(--fc-text-primary)", background:"var(--fc-card-bg)", boxShadow: hasError("email")?"0 0 0 3px rgba(239,68,68,0.1)":"none", transition:"all 0.18s" }}
                onFocus={e => { if (!hasError("email")) { e.target.style.borderColor="#2563EB"; e.target.style.boxShadow="0 0 0 3px rgba(79,70,229,0.1)"; } }}
                onBlur={e  => { if (!hasError("email")) { e.target.style.borderColor="var(--fc-border)"; e.target.style.boxShadow="none"; } }} />
            </div>
            {hasError("email") && <div style={{ fontSize:12, color:"#DC2626", marginTop:4, display:"flex", alignItems:"center", gap:4 }}><i className="fa-solid fa-circle-exclamation" style={{fontSize:10}} />{errors.email}</div>}
          </div>
        </Modal>
      )}

      {/* Delete Confirm */}
      {deleteItem && (
        <Modal title="Remove client?" onClose={() => setDeleteItem(null)} maxWidth={380}
          footer={
            <ModalFooter
              onCancel={() => setDeleteItem(null)}
              onConfirm={handleDelete}
              confirmTone="danger"
              confirmBusy={deleting}
              confirmLabel={deleting
                ? <><i className="fa-solid fa-spinner fa-spin" style={{fontSize:11}} /> Removing...</>
                : <><i className="fa-solid fa-trash" style={{fontSize:11}} /> Remove</>}
            />
          }>
          <p style={{ fontSize:13, color:"var(--fc-text-muted)" }}>
            This will remove <strong style={{color:"var(--fc-text-primary)"}}>{(deleteItem.full_name || `${deleteItem.first_name||""} ${deleteItem.last_name||""}`.trim() || "this client")}</strong> from your network.
          </p>
        </Modal>
      )}
    </div>
  );
}